<template>
    <v-dialog v-model="showDialog" max-width="1400" scrollable>
        <v-card class="views-analysis-modal">
            <v-card-title class="analysis-header">
                <div class="analysis-heading">
                    <span class="analysis-icon" aria-hidden="true">
                        <v-icon>mdi-chart-timeline-variant</v-icon>
                    </span>
                    <div>
                        <div class="analysis-kicker">ANÁLISIS DE TRÁFICO</div>
                        <h2 class="analysis-title">Comparativa mensual</h2>
                        <p class="analysis-subtitle mb-0">
                            Explora la evolución por año sin perder el eje de enero a diciembre.
                        </p>
                    </div>
                </div>
                <v-btn class="close-button" icon aria-label="Cerrar análisis extendido" @click="showDialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-5">
                <v-row align="center" class="analysis-filters" no-gutters>
                    <v-col cols="12" sm="4" md="3" class="pr-sm-2 pb-3 pb-sm-0">
                        <v-select
                            v-model="analysisYear"
                            :items="yearOptions"
                            label="Datos del año"
                            outlined
                            dense
                            hide-details
                        />
                    </v-col>
                    <v-col cols="12" sm="4" md="3" class="px-sm-2 pb-3 pb-sm-0">
                        <v-select
                            v-model="analysisMonths"
                            :items="monthsFilterList"
                            label="Rango mensual"
                            outlined
                            dense
                            clearable
                            hide-details
                        />
                    </v-col>
                    <v-col cols="12" sm="4" md="3" class="pl-sm-2 pb-3 pb-sm-0">
                        <v-select
                            v-model="selectedMetric"
                            :items="metricOptions"
                            label="Métrica"
                            outlined
                            dense
                            hide-details
                        />
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex justify-end pt-2 pt-md-0">
                        <v-btn text color="#8061c2" class="mr-2" @click="resetFilters">
                            Restablecer
                        </v-btn>
                        <v-btn
                            depressed
                            color="#8061c2"
                            class="apply-button"
                            :loading="loading"
                            @click="applyFilters"
                        >
                            Aplicar
                        </v-btn>
                    </v-col>
                </v-row>

                <v-alert v-if="errorMessage" type="error" text dense class="mt-4 mb-0">
                    {{ errorMessage }}
                </v-alert>

                <section class="chart-panel mt-5" aria-labelledby="analysis-chart-title">
                    <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                        <div>
                            <h3 id="analysis-chart-title" class="section-title">{{ selectedMetricLabel }}</h3>
                            <p class="section-caption mb-0">Valores mensuales por año seleccionado.</p>
                        </div>
                        <v-progress-circular
                            v-if="loading"
                            indeterminate
                            size="22"
                            width="2"
                            color="#8061c2"
                        />
                    </div>

                    <apexchart
                        type="line"
                        height="360"
                        :options="chartOptions"
                        :series="chartSeries"
                    />
                </section>

                <section class="table-panel mt-5" aria-labelledby="analysis-table-title">
                    <div class="d-flex align-center justify-space-between mb-3">
                        <div>
                            <h3 id="analysis-table-title" class="section-title">Detalle numérico</h3>
                            <p class="section-caption mb-0">Comparación exacta por mes y año.</p>
                        </div>
                        <span class="record-count">{{ tableRows.length }} meses</span>
                    </div>

                    <div class="table-scroll">
                        <v-simple-table class="comparison-table">
                            <thead>
                                <tr>
                                    <th>Mes</th>
                                    <th v-for="year in tableYears" :key="year" class="text-right">
                                        {{ year }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in tableRows" :key="row.month">
                                    <td class="month-cell">{{ row.month }}</td>
                                    <td v-for="year in tableYears" :key="`${row.month}-${year}`" class="text-right value-cell">
                                        {{ formatNumber(row.values[year]) }}
                                    </td>
                                </tr>
                                <tr v-if="!tableYears.length">
                                    <td colspan="2" class="empty-cell">No hay datos para los filtros seleccionados.</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </div>
                </section>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from '@/axios.js';
import VueApexCharts from 'vue-apexcharts';

export default {
    components: {
        apexchart: VueApexCharts
    },
    props: {
        dialogShow: {
            type: Boolean,
            required: true
        },
        year: {
            default: 'Todos'
        },
        monthsFilter: {
            type: Number,
            default: null
        },
        chartData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            analysisYear: 'Todos',
            analysisMonths: null,
            selectedMetric: 'total_views',
            analysisData: {},
            loading: false,
            errorMessage: null,
            monthsFilterList: [
                { text: 'Últimos 3 meses', value: 3 },
                { text: 'Últimos 6 meses', value: 6 }
            ],
            metricOptions: [
                { text: 'Vistas Totales', value: 'total_views' },
                { text: 'Vistas Únicas', value: 'unique_views' },
                { text: 'Clics Totales', value: 'total_clicks' },
                { text: 'Clics Únicos', value: 'unique_clicks' }
            ],
            colors: [
                '#8061C2', '#40D756', '#4DBBFF', '#FF9F68', '#E85AAD',
                '#1E9E8A', '#F2C14E', '#5B6EE1', '#C75CFF', '#4D7CFE',
                '#D95F59', '#70B77E'
            ]
        };
    },
    computed: {
        showDialog: {
            get() {
                return this.dialogShow;
            },
            set(value) {
                this.$emit('update:dialogShow', value);
            }
        },
        normalizedData() {
            return this.analysisData && typeof this.analysisData === 'object'
                ? this.analysisData
                : {};
        },
        yearOptions() {
            const years = Array.isArray(this.normalizedData.available_years)
                ? this.normalizedData.available_years.map(year => Number(year))
                : [];

            const selectedYear = Number(this.year);
            if (Number.isInteger(selectedYear) && !years.includes(selectedYear)) {
                years.push(selectedYear);
            }

            return ['Todos', ...years.sort((a, b) => b - a)];
        },
        selectedMetricLabel() {
            const metric = this.metricOptions.find(item => item.value === this.selectedMetric);
            return metric ? metric.text : 'Vistas Totales';
        },
        tableYears() {
            return (this.normalizedData.metrics_by_year || [])
                .map(item => Number(item.year))
                .sort((a, b) => a - b);
        },
        tableRows() {
            const labels = this.normalizedData.labels || [];
            const dataByYear = this.normalizedData.metrics_by_year || [];

            return labels.map((month, monthIndex) => {
                const values = {};
                dataByYear.forEach(yearData => {
                    values[yearData.year] = Number(yearData[this.selectedMetric]?.[monthIndex] || 0);
                });

                return {
                    month,
                    values
                };
            });
        },
        chartSeries() {
            return (this.normalizedData.metrics_by_year || []).map((yearData, index) => ({
                name: String(yearData.year),
                data: yearData[this.selectedMetric] || [],
                color: this.colors[index % this.colors.length]
            }));
        },
        chartOptions() {
            return {
                chart: {
                    id: 'views-analysis-chart',
                    toolbar: { show: true },
                    animations: { enabled: true }
                },
                colors: this.colors,
                xaxis: {
                    categories: this.normalizedData.labels || [],
                    labels: {
                        rotate: -35,
                        style: { colors: '#5d6070', fontSize: '12px' }
                    }
                },
                yaxis: {
                    labels: {
                        formatter: value => this.formatNumber(value)
                    }
                },
                stroke: {
                    curve: 'smooth',
                    width: 3
                },
                markers: {
                    size: 4,
                    hover: { size: 6 }
                },
                grid: {
                    borderColor: '#ebe8f3',
                    strokeDashArray: 4
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'center'
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: value => this.formatNumber(value)
                    }
                }
            };
        }
    },
    watch: {
        showDialog(newValue) {
            if (newValue) {
                this.analysisYear = this.year;
                this.analysisMonths = this.monthsFilter;
                this.analysisData = this.chartData || {};
                this.errorMessage = null;
            }
        },
        chartData: {
            deep: true,
            handler(newValue) {
                if (this.showDialog && this.analysisYear === this.year && this.analysisMonths === this.monthsFilter) {
                    this.analysisData = newValue || {};
                }
            }
        }
    },
    methods: {
        formatNumber(value) {
            return new Intl.NumberFormat('en-US', {
                maximumFractionDigits: 0
            }).format(Number(value) || 0);
        },
        async applyFilters() {
            this.loading = true;
            this.errorMessage = null;

            try {
                const response = await axios.get('/admin/get-view-statistics', {
                    params: {
                        year: this.analysisYear,
                        months_filter: this.analysisMonths
                    }
                });
                this.analysisData = response.data.views_chart || {};
            } catch (error) {
                this.errorMessage = 'No se pudo cargar el análisis extendido.';
                console.error('Error fetching extended view statistics:', error);
            } finally {
                this.loading = false;
            }
        },
        resetFilters() {
            this.analysisYear = this.year;
            this.analysisMonths = this.monthsFilter;
            this.selectedMetric = 'total_views';
            this.analysisData = this.chartData || {};
            this.errorMessage = null;
        }
    }
};
</script>

<style scoped>
.views-analysis-modal {
    background: #f7f8fb;
    border-radius: 16px !important;
    overflow: hidden;
}

.analysis-header {
    align-items: flex-start;
    background: #fff;
    border-bottom: 1px solid #ebe9f1;
    color: #2d2a3d;
    display: flex;
    justify-content: space-between;
    padding: 25px 30px 22px;
    position: relative;
}

.analysis-header::before {
    background: #8061c2;
    content: '';
    height: 4px;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
}

.analysis-heading {
    align-items: center;
    display: flex;
    gap: 14px;
}

.analysis-icon {
    align-items: center;
    background: #f1ecfb;
    border: 1px solid #e3d9f7;
    border-radius: 12px;
    color: #8061c2;
    display: inline-flex;
    height: 46px;
    justify-content: center;
    width: 46px;
}

.analysis-icon .v-icon {
    color: #8061c2;
    font-size: 25px;
}

.close-button {
    background: #f5f4f8;
    color: #716c7c !important;
    margin: 1px 0 0 16px;
    transition: background-color 160ms ease, color 160ms ease;
}

.close-button:hover {
    background: #ebe7f3;
    color: #4e4660 !important;
}

.analysis-kicker {
    color: #8061c2;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.16em;
}

.analysis-title {
    color: #302b42;
    font-size: 25px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.15;
    margin: 5px 0 6px;
}

.analysis-subtitle,
.section-caption {
    color: #777487;
    font-size: 13px;
}

.analysis-subtitle {
    color: #777487;
}

.analysis-filters {
    background: #fff;
    border: 1px solid #e5e3eb;
    border-radius: 12px;
    box-shadow: 0 4px 14px rgba(51, 43, 73, 0.04);
    padding: 13px 14px;
}

.apply-button {
    border-radius: 8px;
    color: #fff !important;
    min-width: 88px;
}

.chart-panel,
.table-panel {
    background: #fff;
    border: 1px solid #e8e6ee;
    border-radius: 15px;
    box-shadow: 0 4px 18px rgba(51, 43, 73, 0.035);
    padding: 20px 22px;
}

.section-title {
    color: #332b49;
    font-size: 17px;
    font-weight: 600;
    margin: 0 0 4px;
}

.record-count {
    background: #f1ecfb;
    border: 1px solid #e3d9f7;
    border-radius: 999px;
    color: #8061c2;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 10px;
}

.table-scroll {
    overflow-x: auto;
}

.comparison-table {
    min-width: 520px;
}

.comparison-table th {
    background: #faf9fc;
    border-bottom: 1px solid #e8e6ee !important;
    color: #6a6477 !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.comparison-table td {
    border-bottom: 1px solid #f0edf4 !important;
    color: #403a4e !important;
    font-size: 13px;
}

::v-deep .analysis-filters .v-input__slot {
    background: #fcfcfd;
    border-radius: 8px;
}

::v-deep .analysis-filters .v-label,
::v-deep .analysis-filters .v-select__selection {
    color: #514b5e;
}

::v-deep .analysis-filters .v-input--is-focused .v-input__slot {
    border-color: #8061c2 !important;
}

::v-deep .analysis-filters .v-input--is-focused .v-label {
    color: #8061c2 !important;
}

.month-cell {
    font-weight: 600;
}

.value-cell {
    font-variant-numeric: tabular-nums;
}

.empty-cell {
    color: #898393 !important;
    padding: 22px !important;
    text-align: center;
}

@media (max-width: 600px) {
    .analysis-header {
        padding: 22px 20px 19px;
    }

    .analysis-heading {
        align-items: flex-start;
        gap: 10px;
    }

    .analysis-icon {
        height: 38px;
        width: 38px;
    }

    .analysis-icon .v-icon {
        font-size: 21px;
    }

    .analysis-title {
        font-size: 21px;
    }

    .chart-panel,
    .table-panel {
        padding: 14px 12px;
    }
}
</style>
